'use client';

import { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

/**
 * SSR-safe reduced-motion detection, with a manual override.
 *
 * `framer-motion`'s `useReducedMotion()` returns `null` on the server (no
 * `matchMedia`) but the real preference on the client. Branching the rendered
 * element — or its animation `initial` — on that raw value makes the server
 * HTML differ from the first client render, which triggers a React hydration
 * mismatch (and, when the branch changes element structure, can discard the
 * whole tree, as happened with the public lead graphics).
 *
 * This hook returns `false` during SSR and the first client render so the
 * markup matches, then applies the real input after mount. There are two
 * inputs once mounted: the OS `prefers-reduced-motion` setting, and the
 * footer "Pause animations" control (session-23 P9), which stores
 * `mnrq-motion: paused` in localStorage and dispatches
 * `mnrq-motion-change`. Either one degrades every public animation.
 */

export const MOTION_STORAGE_KEY = 'mnrq-motion';
export const MOTION_CHANGE_EVENT = 'mnrq-motion-change';

/** Read the stored footer-control preference. False when unset or unavailable. */
export function isMotionPaused(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(MOTION_STORAGE_KEY) === 'paused';
  } catch {
    return false;
  }
}

/** Persist the footer-control preference, mirror it onto <html data-motion>,
 *  and notify every subscribed hook/component in this tab. */
export function setMotionPaused(paused: boolean): void {
  try {
    if (paused) {
      window.localStorage.setItem(MOTION_STORAGE_KEY, 'paused');
    } else {
      window.localStorage.removeItem(MOTION_STORAGE_KEY);
    }
  } catch {
    /* private mode / storage blocked — the session still works */
  }
  if (typeof document !== 'undefined') {
    if (paused) {
      document.documentElement.dataset.motion = 'paused';
    } else {
      delete document.documentElement.dataset.motion;
    }
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(MOTION_CHANGE_EVENT));
  }
}

/** Apply the stored preference to <html> without notifying anyone. Used on
 *  mount so a paused visitor gets the CSS backstop immediately. */
function syncDataAttribute(paused: boolean): void {
  if (typeof document === 'undefined') return;
  if (paused) {
    document.documentElement.dataset.motion = 'paused';
  } else {
    delete document.documentElement.dataset.motion;
  }
}

export function useSafeReducedMotion(): boolean {
  const reduce = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  const [storedPaused, setStoredPaused] = useState(false);

  useEffect(() => {
    setMounted(true);
    /* Read the stored value here, not during render: reading localStorage
       during render is what the hydration-mismatch contract above forbids. */
    const initial = isMotionPaused();
    setStoredPaused(initial);
    syncDataAttribute(initial);

    const onChange = () => {
      const paused = isMotionPaused();
      setStoredPaused(paused);
      syncDataAttribute(paused);
    };
    window.addEventListener(MOTION_CHANGE_EVENT, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(MOTION_CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  if (!mounted) return false;
  return !!reduce || storedPaused;
}
